import { App_ApplicationFragment, App_PortalFragment, useAppQuery } from '../generated.graphql';
import React from 'react';
import { useRouteMatch } from 'react-router';
import { getInit } from 'shared/urql';


interface PortalContextData {
  portal: App_PortalFragment;
  portalSlug: string;
  portalDoesNotExist: boolean;
  application: App_ApplicationFragment | null;
  buildLink: (path?: string) => string;
}

export interface AppContextData extends PortalContextData {
  application: App_ApplicationFragment
}

export const AppContext = React.createContext<PortalContextData | null>(null);

interface AppLoading {
  loading: boolean;
}

interface NetworkError {
  networkError: any;
}

interface GraphQLErrors {
  graphQLErrors: any;
}

interface OtherError {
  otherError: any;
}

interface PortalDoesNotExistError {
  portalSlug: string | null,
}

interface ApplicationDoesNotExist {

}

interface Config {

}

// Type guards for AppContextData seem heavy on the boilerplate, maybe there's a better way?

export type AppContextProviderResult = PortalContextData | AppLoading | PortalDoesNotExistError | ApplicationDoesNotExist | NetworkError | GraphQLErrors | OtherError | null;

export function isPortalContextData(value: AppContextProviderResult): value is PortalContextData {
  return (value as PortalContextData).portal !== undefined;
}

export function isAppContextData(value: AppContextProviderResult): value is AppContextData {
  return (value as AppContextData).application !== undefined && (value as AppContextData).application !== null;
}

export function isAppLoading(value: AppContextProviderResult): value is AppLoading {
  return (value as AppLoading).loading !== undefined;
}

export function isNetworkError(value: AppContextProviderResult): value is NetworkError {
  return (value as NetworkError).networkError !== undefined;
}

export function isGraphQLError(value: AppContextProviderResult): value is GraphQLErrors {
  return (value as GraphQLErrors).graphQLErrors !== undefined;
}

export function isOtherError(value: AppContextProviderResult): value is OtherError {
  return (value as OtherError).otherError !== undefined;
}

export function isPortalDoesNotExistError(value: AppContextProviderResult): value is AppContextData {
  return (value as PortalDoesNotExistError).portalSlug !== undefined;
}

export const usePortalSlug = (): string | null => {
  const route = useRouteMatch<{portalSlug: string}>('/:portalSlug([-\\w]+)');
  const portalSlug = route?.params.portalSlug || null;
  return portalSlug;
}

export const useAppContextProvider = (config: Config = {}): AppContextProviderResult => {
  const portalSlug = usePortalSlug();
  const [result, reexecuteQuery] = useAppQuery({
    variables: {portalSlug: portalSlug || ''},
    pause: portalSlug === null,
  })

  // If there are errors on the main AppQuery, such as an invalid portalSlug
  // result.error = {"name":"CombinedError","message":"[Network] NetworkError when attempting to fetch resource.","graphQLErrors":[],"networkError":{}}
  const errorCodes = result.error?.graphQLErrors.map(e => e.extensions?.errorCode);
  const networkError = !!(result.error?.networkError || result.error?.message?.includes("NetworkError"));
  const graphQLErrors = !!result.error?.graphQLErrors;
  const otherError = !!result.error;
  const portalDoesNotExist = !portalSlug || errorCodes?.includes('PortalDoesNotExist') || false

  const portal = result.data?.portal || null
  const application = result.data?.portal?.myApplication || null

  // Manual refresh of query since urql 2.0.0 removed pollInterval
  React.useEffect(() => {
    if (!result.fetching) {
      // on subsequent refreshes, don't extend app-expiry
      const id = setTimeout(() =>
          reexecuteQuery({ requestPolicy: 'network-only', fetchOptions: getInit({}, {}, { ExtendSession: false }) }), 3 * 60 * 1000);
      return () => clearTimeout(id);
    }
  }, [result.fetching, reexecuteQuery]);

  // const [state, setState] = React.useState({
  // })

  if (portal && portalSlug) {
    return {
      // ...state,
      portal,
      portalSlug,
      portalDoesNotExist,
      application,
      buildLink: (path: string) => path ? `/${portalSlug}/${path}/` : `/${portalSlug}/`,
    };
  } else if (result.fetching) {
    return { loading: true };
  } else if (networkError) {
    return { networkError: result.error?.networkError };
  } else if (graphQLErrors) {
    return { graphQLErrors: result.error?.graphQLErrors };
  } else if (otherError) {
    return { otherError: result.error };
  } else if (!portal) {
    return { portalSlug };
  } else if (!application) {
    return {};
  }

  return null;
}

export const usePortalContext = () => {
  const value = React.useContext(AppContext);
  if (!isPortalContextData(value)) {
    throw new Error('You probably forgot to put <AppContext.Provider> above this component.')
  }
  return value
}

export const useAppContext = () => {
  const value = React.useContext(AppContext);
  if (!isAppContextData(value)) {
    throw new Error('You probably forgot to put <AppContext.Provider> above this component.')
  }
  return value
}

